import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, Redirect } from "wouter";
import { useClerk } from "@clerk/react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useGetMe,
  useGetMyOnboarding,
  useUpdateMyOnboarding,
  useSubmitMyOnboarding,
  useGetMyMsa,
  useSignMyMsa,
  getGetMyOnboardingQueryKey,
  getGetMeQueryKey,
  getGetMyMsaQueryKey,
} from "@workspace/api-client-react";
import { useUpload } from "@workspace/object-storage-web";
import { SA_BANKS, branchCodeForBank } from "@/lib/saBanks";
import { DOCUMENT_LABELS, rejectionReasonLabel } from "@/lib/ficaReview";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { MsaContractDocument } from "@/components/onboarding/MsaContractDocument";
import { SignaturePad } from "@/components/onboarding/SignaturePad";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import {
  AlertTriangle,
  Camera,
  Check,
  ChevronLeft,
  ChevronRight,
  FileUp,
  Loader2,
  LogOut,
  PenLine,
  ShieldCheck,
  UploadCloud,
  UserRound,
} from "lucide-react";

type FormState = {
  fullName: string;
  idNumber: string;
  phone: string;
  addressLine: string;
  city: string;
  province: string;
  postalCode: string;
  preferredOperatingArea: string;
  agentType: string;
  bankName: string;
  accountNumber: string;
  accountType: string;
  branchCode: string;
  idDocumentPath: string;
  proofOfAddressPath: string;
  bankConfirmationPath: string;
  profilePhotoPath: string;
};

const EMPTY: FormState = {
  fullName: "",
  idNumber: "",
  phone: "",
  addressLine: "",
  city: "",
  province: "",
  postalCode: "",
  preferredOperatingArea: "",
  agentType: "",
  bankName: "",
  accountNumber: "",
  accountType: "",
  branchCode: "",
  idDocumentPath: "",
  proofOfAddressPath: "",
  bankConfirmationPath: "",
  profilePhotoPath: "",
};

const PROVINCES = [
  "Eastern Cape",
  "Free State",
  "Gauteng",
  "KwaZulu-Natal",
  "Limpopo",
  "Mpumalanga",
  "North West",
  "Northern Cape",
  "Western Cape",
];

type StepKey = "details" | "banking" | "fica" | "agreement" | "review";

// The agent-onboarding wizard steps. The "agreement" step (MSA e-signing) is
// inserted only for field-sales agents, who must sign the Field Sales Agent
// Agreement before they can submit; other agent types skip it entirely.
const STEP_DEFS: { key: StepKey; label: string }[] = [
  { key: "details", label: "Your details" },
  { key: "banking", label: "Banking" },
  { key: "fica", label: "FICA documents" },
  { key: "agreement", label: "Agreement" },
  { key: "review", label: "Review" },
];

const stepCountFor = (agentType: string): number =>
  agentType === "field_sales" ? STEP_DEFS.length : STEP_DEFS.length - 1;

const AGENT_TYPE_OPTIONS = [
  { value: "field_sales", label: "Independent Field Sales Agent" },
  {
    value: "independent_technician",
    label: "Independent Technician Referral Agent",
  },
] as const;

function agentTypeLabel(value: string): string {
  return AGENT_TYPE_OPTIONS.find((o) => o.value === value)?.label ?? "";
}

// Auto-save draft storage. Keyed per user so two applicants sharing a device
// never see each other's in-progress data. Bumped if the draft shape changes.
const DRAFT_STORAGE_PREFIX = "mooya:onboarding-draft:v1:";

function draftStorageKey(userId: number | undefined): string | null {
  return typeof userId === "number" ? `${DRAFT_STORAGE_PREFIX}${userId}` : null;
}

type OnboardingDraft = { form: FormState; step: number };

export function OnboardingWizard() {
  const [, setLocation] = useLocation();
  const { signOut } = useClerk();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: me } = useGetMe();
  const { data: onboarding, isLoading } = useGetMyOnboarding();
  const updateMutation = useUpdateMyOnboarding();
  const submitMutation = useSubmitMyOnboarding();
  const photoRequired = onboarding?.profilePhotoRequired ?? false;
  const { data: msa } = useGetMyMsa();

  const [step, setStep] = useState(0);
  const [form, setForm] = useState<FormState>(EMPTY);
  const seeded = useRef(false);
  // Gates the localStorage auto-save: we must not write the empty initial state
  // over a stored draft before hydration (server seed + draft restore) lands.
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    // Seed once, and only after BOTH the onboarding record and the current user
    // (whose id keys the per-user draft) have loaded.
    if (!onboarding || !me || seeded.current) return;
    seeded.current = true;
    const serverForm: FormState = {
      fullName: onboarding.fullName ?? "",
      idNumber: onboarding.idNumber ?? "",
      phone: onboarding.phone ?? "",
      addressLine: onboarding.addressLine ?? "",
      city: onboarding.city ?? "",
      province: onboarding.province ?? "",
      postalCode: onboarding.postalCode ?? "",
      preferredOperatingArea: onboarding.preferredOperatingArea ?? "",
      agentType: onboarding.agentType ?? "",
      bankName: onboarding.bankName ?? "",
      accountNumber: onboarding.accountNumber ?? "",
      accountType: onboarding.accountType ?? "",
      branchCode: onboarding.branchCode ?? "",
      idDocumentPath: onboarding.idDocumentPath ?? "",
      proofOfAddressPath: onboarding.proofOfAddressPath ?? "",
      bankConfirmationPath: onboarding.bankConfirmationPath ?? "",
      profilePhotoPath: onboarding.profilePhotoPath ?? "",
    };
    let nextForm = serverForm;
    let nextStep =
      typeof onboarding.currentStep === "number"
        ? Math.min(
            onboarding.currentStep,
            stepCountFor(serverForm.agentType) - 1,
          )
        : 0;

    // Restore a locally-saved draft (unsaved edits from a previous session that
    // closed the tab mid-step). The draft is the freshest copy of the same form,
    // so its fields win over the server seed.
    const key = draftStorageKey(me.id);
    if (key) {
      try {
        const raw = localStorage.getItem(key);
        if (raw) {
          const saved = JSON.parse(raw) as Partial<OnboardingDraft>;
          if (saved.form && typeof saved.form === "object") {
            nextForm = { ...serverForm, ...saved.form };
          }
          if (typeof saved.step === "number") {
            nextStep = Math.min(
              Math.max(saved.step, 0),
              stepCountFor(nextForm.agentType) - 1,
            );
          }
        }
      } catch {
        // Corrupt/unreadable draft — fall back to the server state.
      }
    }

    setForm(nextForm);
    setStep(nextStep);
    setHydrated(true);
  }, [onboarding, me]);

  // Mirror the latest form/step into a ref so the synchronous flush handlers
  // (tab hidden/closed, unmount) always read fresh values without re-binding.
  const draftRef = useRef<OnboardingDraft>({ form, step });
  useEffect(() => {
    draftRef.current = { form, step };
  }, [form, step]);

  // Whether the application can still be edited here. Kept in a ref so the
  // debounced save, flush handler, and unmount cleanup never write a draft for a
  // submitted/approved/rejected/linked application. The same transition also
  // drops any stale stored draft.
  const editableRef = useRef(true);
  useEffect(() => {
    const status = onboarding?.status;
    const editable =
      me?.agentId == null &&
      status !== "approved" &&
      status !== "submitted" &&
      status !== "rejected";
    editableRef.current = editable;
    if (!editable) {
      const key = draftStorageKey(me?.id);
      if (key) {
        try {
          localStorage.removeItem(key);
        } catch {
          // ignore
        }
      }
    }
  }, [me?.id, me?.agentId, onboarding?.status]);

  // Debounced auto-save of the in-progress form so closing the tab mid-step
  // never loses typed data. Runs only after hydration.
  useEffect(() => {
    if (!hydrated) return;
    const key = draftStorageKey(me?.id);
    if (!key) return;
    const handle = setTimeout(() => {
      // Re-check writability at fire time: the app may have become non-editable
      // (e.g. just submitted) during the debounce window.
      if (!editableRef.current) return;
      try {
        localStorage.setItem(key, JSON.stringify({ form, step }));
      } catch {
        // Storage unavailable/full — best-effort only.
      }
    }, 500);
    return () => clearTimeout(handle);
  }, [form, step, hydrated, me?.id]);

  // Synchronous flush of the freshest draft — used when the tab is hidden/closed
  // or the wizard unmounts, so an edit made inside the 500ms debounce window is
  // never lost (the original "close browser mid-step" failure mode).
  const flushDraft = useCallback(() => {
    if (!hydrated || !editableRef.current) return;
    const key = draftStorageKey(me?.id);
    if (!key) return;
    try {
      localStorage.setItem(key, JSON.stringify(draftRef.current));
    } catch {
      // best-effort
    }
  }, [hydrated, me?.id]);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "hidden") flushDraft();
    };
    window.addEventListener("pagehide", flushDraft);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("pagehide", flushDraft);
      document.removeEventListener("visibilitychange", onVisibility);
      // Flush on unmount (e.g. route change) too.
      flushDraft();
    };
  }, [flushDraft]);

  const set = (key: keyof FormState, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  // Selecting a bank also fixes the (universal) branch code; the agent never
  // edits the branch code directly.
  const selectBank = (name: string) =>
    setForm((prev) => ({
      ...prev,
      bankName: name,
      branchCode: branchCodeForBank(name),
    }));

  // Field-sales agents get an extra "agreement" step (MSA signing) before
  // review; the step list is derived from the chosen agent type. clampedStep
  // keeps a persisted/draft step index in range if the type (and therefore the
  // step count) changed since it was saved.
  const isFieldSales = form.agentType === "field_sales";
  const steps = useMemo(
    () => STEP_DEFS.filter((d) => d.key !== "agreement" || isFieldSales),
    [isFieldSales],
  );
  const clampedStep = Math.min(step, steps.length - 1);
  const currentKey = steps[clampedStep]?.key ?? "details";

  const persist = async (nextStep: number) => {
    await updateMutation.mutateAsync({
      data: { ...form, currentStep: nextStep },
    });
    queryClient.invalidateQueries({ queryKey: getGetMyOnboardingQueryKey() });
    // Editing a contract particular may have invalidated a prior MSA signature
    // server-side; refresh so the Agreement step reflects any re-sign needed.
    queryClient.invalidateQueries({ queryKey: getGetMyMsaQueryKey() });
  };

  const stepValid = useMemo(() => {
    if (currentKey === "details") {
      return Boolean(
        form.fullName &&
        form.idNumber &&
        form.phone &&
        form.addressLine &&
        form.city &&
        form.province &&
        form.postalCode &&
        form.preferredOperatingArea &&
        form.agentType &&
        (!photoRequired || form.profilePhotoPath),
      );
    }
    if (currentKey === "banking") {
      return Boolean(
        form.bankName &&
        form.accountNumber &&
        form.accountType &&
        form.branchCode,
      );
    }
    if (currentKey === "fica") {
      return Boolean(
        form.idDocumentPath &&
        form.proofOfAddressPath &&
        form.bankConfirmationPath,
      );
    }
    if (currentKey === "agreement") {
      // The MSA must be signed before the agent can leave the agreement step.
      return Boolean(msa?.agentSigned);
    }
    return true;
  }, [currentKey, form, photoRequired, msa?.agentSigned]);

  const goNext = async () => {
    const next = Math.min(clampedStep + 1, steps.length - 1);
    try {
      await persist(next);
      setStep(next);
    } catch {
      toast({
        title: "Could not save progress",
        description: "Please check your connection and try again.",
        variant: "destructive",
      });
    }
  };

  const goBack = () => setStep(Math.max(clampedStep - 1, 0));

  const submit = async () => {
    try {
      await persist(steps.length - 1);
      const submitted = await submitMutation.mutateAsync();
      // Lock drafting synchronously so neither the unmount flush nor a pending
      // debounce can rewrite the draft we are about to clear.
      editableRef.current = false;
      // Seed the cache with the submitted state synchronously so the onboarding
      // gate sees status="submitted" immediately and does not bounce us back to
      // /onboarding before the refetch lands.
      queryClient.setQueryData(getGetMyOnboardingQueryKey(), submitted);
      queryClient.invalidateQueries({ queryKey: getGetMyOnboardingQueryKey() });
      queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() });
      // The application is now locked; drop the local draft so a later visit
      // never restores stale pre-submission edits.
      const key = draftStorageKey(me?.id);
      if (key) {
        try {
          localStorage.removeItem(key);
        } catch {
          // ignore
        }
      }
      toast({
        title: "Application submitted",
        description: "Your onboarding is now under review.",
      });
      setLocation("/home");
    } catch (err) {
      toast({
        title: "Submission failed",
        description:
          err instanceof Error
            ? err.message
            : "Please complete every field and upload all documents.",
        variant: "destructive",
      });
    }
  };

  // Hold the loader until the form has hydrated (server seed + any draft
  // restore). Rendering the editable form before then would let edits typed
  // during a slow `me` fetch be overwritten when hydration lands.
  if (isLoading || !hydrated) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-gray-50 text-slate-400">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }

  // The wizard is for applicants who can still edit. Established (linked) or
  // approved agents go to the app; submitted/rejected applicants see the
  // read-only status page. Only in_progress / changes_requested edit here.
  if (me?.agentId != null || onboarding?.status === "approved") {
    return <Redirect to="/home" />;
  }
  if (onboarding?.status === "submitted" || onboarding?.status === "rejected") {
    return <Redirect to="/onboarding/status" />;
  }

  const saving = updateMutation.isPending;
  const submitting = submitMutation.isPending;
  const changesRequested = onboarding?.status === "changes_requested";
  const rejectedDocs = (onboarding?.documentReviews ?? []).filter(
    (d) => d.status === "rejected",
  );
  const reviewFor = (key: string) =>
    rejectedDocs.find((d) => d.documentKey === key);

  return (
    <div className="font-sans min-h-[100dvh] w-full max-w-[480px] mx-auto bg-gray-50 flex flex-col text-[#0B132B]">
      <header className="bg-[#0B132B] text-white px-5 pt-6 pb-5">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <img
              src={`${import.meta.env.BASE_URL}mooya-mark.png`}
              alt="Mooya"
              className="w-8 h-8 object-contain"
            />
            <span className="font-bold text-lg tracking-tight">Mooya</span>
          </div>
          <button
            onClick={() => signOut()}
            className="flex items-center gap-1.5 text-xs text-slate-300 hover:text-white transition-colors"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
        <h1 className="text-xl font-bold mb-1">Agent onboarding</h1>
        <p className="text-sm text-slate-300">
          Step {clampedStep + 1} of {steps.length} — {steps[clampedStep].label}
        </p>
        <div className="mt-4 flex gap-1.5">
          {steps.map((s, i) => (
            <div
              key={s.key}
              className={`h-1.5 flex-1 rounded-full transition-colors ${
                i <= clampedStep ? "bg-[#E6007E]" : "bg-white/20"
              }`}
            />
          ))}
        </div>
      </header>

      <main className="flex-1 overflow-y-auto px-5 py-6 space-y-4">
        {changesRequested && (
          <div className="flex items-start gap-3 rounded-xl bg-amber-50 border border-amber-200 p-4">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-amber-900">
                Your account manager requested changes
              </p>
              {onboarding?.reviewerNotes ? (
                <p className="text-sm text-amber-800 mt-1 whitespace-pre-line">
                  {onboarding.reviewerNotes}
                </p>
              ) : null}
              {rejectedDocs.length > 0 ? (
                <div className="mt-2 space-y-1.5">
                  <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">
                    Documents to re-upload
                  </p>
                  <ul className="space-y-1.5">
                    {rejectedDocs.map((d) => (
                      <li
                        key={d.documentKey}
                        className="rounded-lg bg-white/70 border border-amber-100 px-3 py-2"
                      >
                        <p className="text-sm font-medium text-amber-900">
                          {DOCUMENT_LABELS[d.documentKey]}
                        </p>
                        <p className="text-xs text-amber-800 mt-0.5">
                          {rejectionReasonLabel(d.rejectionReason)}
                        </p>
                        {d.comment ? (
                          <p className="text-xs text-amber-700 mt-0.5 whitespace-pre-line">
                            {d.comment}
                          </p>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              <p className="text-xs text-amber-700 mt-2">
                Update the details below and resubmit your application.
              </p>
            </div>
          </div>
        )}
        {currentKey === "details" && (
          <>
            <AvatarUploadField
              value={form.profilePhotoPath}
              required={photoRequired}
              onUploaded={(path) => set("profilePhotoPath", path)}
            />
            <Field label="Full name">
              <Input
                value={form.fullName}
                onChange={(e) => set("fullName", e.target.value)}
                placeholder="As it appears on your ID"
              />
            </Field>
            <Field label="South African ID number">
              <Input
                value={form.idNumber}
                onChange={(e) => set("idNumber", e.target.value)}
                inputMode="numeric"
                placeholder="13 digits"
              />
            </Field>
            <Field label="Mobile number">
              <Input
                value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
                inputMode="tel"
                placeholder="082 123 4567"
              />
            </Field>
            <Field label="Residential address">
              <Input
                value={form.addressLine}
                onChange={(e) => set("addressLine", e.target.value)}
                placeholder="Street address"
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="City">
                <Input
                  value={form.city}
                  onChange={(e) => set("city", e.target.value)}
                />
              </Field>
              <Field label="Postal code">
                <Input
                  value={form.postalCode}
                  onChange={(e) => set("postalCode", e.target.value)}
                  inputMode="numeric"
                />
              </Field>
            </div>
            <Field label="Province">
              <Select
                value={form.province}
                onValueChange={(v) => set("province", v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select province" />
                </SelectTrigger>
                <SelectContent>
                  {PROVINCES.map((p) => (
                    <SelectItem key={p} value={p}>
                      {p}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Preferred operating area">
              <Input
                value={form.preferredOperatingArea}
                onChange={(e) => set("preferredOperatingArea", e.target.value)}
                placeholder="e.g. Sandton, Soweto, central Durban"
              />
            </Field>
            <Field label="Agent type">
              <Select
                value={form.agentType}
                onValueChange={(v) => set("agentType", v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {AGENT_TYPE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </>
        )}

        {currentKey === "banking" && (
          <>
            <Field label="Bank name">
              <Select value={form.bankName} onValueChange={selectBank}>
                <SelectTrigger>
                  <SelectValue placeholder="Select your bank" />
                </SelectTrigger>
                <SelectContent>
                  {SA_BANKS.map((b) => (
                    <SelectItem key={b.name} value={b.name}>
                      {b.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Account number">
              <Input
                value={form.accountNumber}
                onChange={(e) => set("accountNumber", e.target.value)}
                inputMode="numeric"
              />
            </Field>
            <Field label="Account type">
              <Select
                value={form.accountType}
                onValueChange={(v) => set("accountType", v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select account type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cheque">Cheque / Current</SelectItem>
                  <SelectItem value="savings">Savings</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Branch code">
              <Input
                value={form.branchCode}
                readOnly
                disabled
                placeholder="Select a bank first"
                className="bg-gray-50 text-gray-500"
              />
              <p className="text-xs text-gray-500">
                Universal branch code — set automatically from your bank.
              </p>
            </Field>
          </>
        )}

        {currentKey === "fica" && (
          <>
            <p className="text-sm text-gray-500 mb-2">
              Upload clear copies of your FICA documents (PDF or image).
            </p>
            <UploadField
              label="ID document"
              value={form.idDocumentPath}
              onUploaded={(path) => set("idDocumentPath", path)}
              rejection={reviewFor("id_document")}
            />
            <UploadField
              label="Proof of address"
              value={form.proofOfAddressPath}
              onUploaded={(path) => set("proofOfAddressPath", path)}
              rejection={reviewFor("proof_of_address")}
            />
            <UploadField
              label="Bank confirmation letter"
              value={form.bankConfirmationPath}
              onUploaded={(path) => set("bankConfirmationPath", path)}
              rejection={reviewFor("bank_confirmation")}
            />
          </>
        )}

        {currentKey === "agreement" && <MsaAgreementStep />}

        {currentKey === "review" && (
          <div className="space-y-5">
            <div className="flex items-start gap-3 rounded-xl bg-[#00A859]/10 p-4">
              <ShieldCheck className="w-5 h-5 text-[#00A859] mt-0.5 shrink-0" />
              <p className="text-sm text-[#0B132B]">
                Review your details below. Once submitted, your application goes
                to your account manager for approval.
              </p>
            </div>
            <ReviewGroup title="Your details">
              <ReviewRow
                label="Profile photo"
                value={
                  form.profilePhotoPath
                    ? "Uploaded"
                    : photoRequired
                      ? "Required"
                      : "Not added (optional)"
                }
              />
              <ReviewRow label="Full name" value={form.fullName} />
              <ReviewRow label="ID number" value={form.idNumber} />
              <ReviewRow label="Mobile" value={form.phone} />
              <ReviewRow
                label="Address"
                value={`${form.addressLine}, ${form.city}, ${form.province} ${form.postalCode}`}
              />
              <ReviewRow
                label="Operating area"
                value={form.preferredOperatingArea}
              />
              <ReviewRow
                label="Agent type"
                value={agentTypeLabel(form.agentType)}
              />
            </ReviewGroup>
            <ReviewGroup title="Banking">
              <ReviewRow label="Bank" value={form.bankName} />
              <ReviewRow label="Account" value={form.accountNumber} />
              <ReviewRow label="Type" value={form.accountType} />
            </ReviewGroup>
            <ReviewGroup title="Documents">
              <ReviewRow
                label="ID document"
                value={form.idDocumentPath ? "Uploaded" : "Missing"}
              />
              <ReviewRow
                label="Proof of address"
                value={form.proofOfAddressPath ? "Uploaded" : "Missing"}
              />
              <ReviewRow
                label="Bank confirmation"
                value={form.bankConfirmationPath ? "Uploaded" : "Missing"}
              />
            </ReviewGroup>
          </div>
        )}
      </main>

      <footer className="border-t border-gray-100 bg-white px-5 py-4 flex items-center gap-3">
        {clampedStep > 0 && (
          <Button
            variant="outline"
            onClick={goBack}
            className="flex-1 rounded-xl py-6"
            disabled={saving || submitting}
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Back
          </Button>
        )}
        {clampedStep < steps.length - 1 ? (
          <Button
            onClick={goNext}
            disabled={!stepValid || saving}
            className="flex-1 bg-[#E6007E] hover:bg-[#c4006b] text-white rounded-xl py-6 font-semibold"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                Continue <ChevronRight className="w-4 h-4 ml-1" />
              </>
            )}
          </Button>
        ) : (
          <Button
            onClick={submit}
            disabled={
              submitting || saving || (isFieldSales && !msa?.agentSigned)
            }
            className="flex-1 bg-[#E6007E] hover:bg-[#c4006b] text-white rounded-xl py-6 font-semibold"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              "Submit application"
            )}
          </Button>
        )}
      </footer>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium text-[#0B132B]">{label}</Label>
      {children}
    </div>
  );
}

function UploadField({
  label,
  value,
  onUploaded,
  rejection,
}: {
  label: string;
  value: string;
  onUploaded: (path: string) => void;
  rejection?: { rejectionReason?: string | null; comment?: string | null };
}) {
  const { toast } = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const { uploadFile, isUploading, progress } = useUpload({
    onError: (err) =>
      toast({
        title: "Upload failed",
        description: err.message,
        variant: "destructive",
      }),
  });

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const result = await uploadFile(file);
    if (result) onUploaded(result.objectPath);
    e.target.value = "";
  };

  const uploaded = Boolean(value);
  const rejected = Boolean(rejection);

  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium text-[#0B132B]">{label}</Label>
      {rejected ? (
        <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-red-700">
            Resubmission required
          </p>
          <p className="text-xs text-red-700 mt-0.5">
            {rejectionReasonLabel(rejection?.rejectionReason)}
          </p>
          {rejection?.comment ? (
            <p className="text-xs text-red-600 mt-0.5 whitespace-pre-line">
              {rejection.comment}
            </p>
          ) : null}
        </div>
      ) : null}
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={isUploading}
        className={`w-full rounded-xl border-2 border-dashed px-4 py-5 flex items-center gap-3 text-left transition-colors ${
          rejected
            ? "border-red-300 bg-red-50/60 hover:border-red-400"
            : uploaded
              ? "border-[#00A859] bg-[#00A859]/5"
              : "border-gray-200 hover:border-[#E6007E]/50 bg-white"
        }`}
      >
        <div
          className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
            rejected
              ? "bg-red-100"
              : uploaded
                ? "bg-[#00A859]/10"
                : "bg-gray-100"
          }`}
        >
          {isUploading ? (
            <Loader2 className="w-5 h-5 animate-spin text-[#E6007E]" />
          ) : rejected ? (
            <AlertTriangle className="w-5 h-5 text-red-500" />
          ) : uploaded ? (
            <Check className="w-5 h-5 text-[#00A859]" />
          ) : (
            <FileUp className="w-5 h-5 text-gray-400" />
          )}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-[#0B132B]">
            {isUploading
              ? `Uploading… ${progress}%`
              : rejected
                ? "Tap to re-upload"
                : uploaded
                  ? "Document uploaded"
                  : "Tap to upload"}
          </p>
          <p className="text-xs text-gray-500 flex items-center gap-1">
            <UploadCloud className="w-3 h-3" />
            {uploaded ? "Tap to replace" : "PDF, JPG or PNG"}
          </p>
        </div>
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,image/*"
        className="hidden"
        onChange={handleChange}
      />
    </div>
  );
}

function AvatarUploadField({
  value,
  onUploaded,
  required,
}: {
  value: string;
  onUploaded: (path: string) => void;
  required: boolean;
}) {
  const { toast } = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [localPreview, setLocalPreview] = useState<string | null>(null);
  const { uploadFile, isUploading } = useUpload({
    onError: (err) =>
      toast({
        title: "Upload failed",
        description: err.message,
        variant: "destructive",
      }),
  });

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLocalPreview(URL.createObjectURL(file));
    const result = await uploadFile(file);
    if (result) onUploaded(result.objectPath);
    e.target.value = "";
  };

  const uploaded = Boolean(value || localPreview);
  const previewSrc = localPreview ?? (value ? `/api/storage${value}` : null);

  return (
    <div className="flex flex-col items-center gap-2.5 pb-1">
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={isUploading}
        className={`relative w-24 h-24 rounded-full flex items-center justify-center overflow-hidden border-2 transition-colors ${
          uploaded
            ? "border-[#00A859] bg-[#00A859]/5"
            : "border-dashed border-gray-300 bg-gray-50 hover:border-[#E6007E]/50"
        }`}
      >
        {previewSrc ? (
          <img
            src={previewSrc}
            alt="Profile"
            className="w-full h-full object-cover"
          />
        ) : (
          <UserRound className="w-9 h-9 text-gray-400" />
        )}
        <span className="absolute bottom-0 right-0 w-7 h-7 rounded-full bg-[#E6007E] flex items-center justify-center border-2 border-white">
          {isUploading ? (
            <Loader2 className="w-3.5 h-3.5 text-white animate-spin" />
          ) : (
            <Camera className="w-3.5 h-3.5 text-white" />
          )}
        </span>
      </button>
      <div className="text-center">
        <p className="text-sm font-medium text-[#0B132B]">
          {uploaded ? "Tap to replace photo" : "Add a profile photo"}
        </p>
        <p className="text-xs text-gray-500">
          {required ? "Required" : "Optional"} · JPG or PNG
        </p>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleChange}
      />
    </div>
  );
}

// MSA e-signing step shown only to field-sales agents in the onboarding wizard.
// Renders the full on-screen agreement (identical to the executed PDF) followed
// by a hand-drawn signature + consent form. The signer's name is taken from the
// registration record (no name re-entry). Signing is a hard gate: the wizard will
// not advance to Review (and the server rejects submit) until the agent signs.
function MsaAgreementStep() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { data: msa } = useGetMyMsa();
  const sign = useSignMyMsa();
  const [signatureImage, setSignatureImage] = useState<string | null>(null);
  const [consent, setConsent] = useState(false);

  if (!msa) {
    return (
      <div className="flex justify-center py-10 text-slate-400">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }

  const registeredName = msa.prefill.fullName?.trim() ?? "";
  const canSign = !!signatureImage && consent && !sign.isPending;

  const doSign = async () => {
    if (!canSign || !signatureImage) return;
    try {
      await sign.mutateAsync({
        data: { signatureImage, consent },
      });
      await queryClient.invalidateQueries({ queryKey: getGetMyMsaQueryKey() });
      toast({
        title: "Agreement signed",
        description: "You can now continue to review and submit.",
      });
    } catch (err) {
      toast({
        title: "Could not sign agreement",
        description: err instanceof Error ? err.message : "Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 rounded-xl border border-[#E6007E]/30 bg-[#E6007E]/5 p-4">
        <PenLine className="w-5 h-5 text-[#E6007E] mt-0.5 shrink-0" />
        <p className="text-sm text-[#0B132B]">
          Please read your Field Sales Agent Agreement in full, then sign below.
          Mooya will counter-sign once your application is approved.
        </p>
      </div>

      <MsaContractDocument
        contract={msa.contract}
        prefill={msa.prefill}
        agentSignedName={msa.agentSignedName}
        agentSignedAt={msa.agentSignedAt}
        agentSignatureImage={msa.agentSignatureImage}
        adminSignedName={msa.adminSignedName}
        adminSignedAt={msa.adminSignedAt}
        adminSignatureImage={msa.adminSignatureImage}
      />

      {msa.agentSigned ? (
        <div className="flex items-start gap-3 rounded-xl bg-[#00A859]/10 p-4">
          <ShieldCheck className="w-5 h-5 text-[#00A859] mt-0.5 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-[#0B132B]">
              You have signed your agreement
            </p>
            <p className="text-sm text-gray-600 mt-1">
              Continue to review and submit your application.
            </p>
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-4">
          <div className="space-y-1.5">
            <Label>Draw your signature to sign</Label>
            <p className="text-xs text-gray-500">
              Signing as{" "}
              <span className="font-semibold text-[#0B132B]">
                {registeredName || "your registered name"}
              </span>{" "}
              — the name on your application.
            </p>
            <SignaturePad
              ariaLabel="Draw your signature"
              onChange={setSignatureImage}
            />
          </div>
          <div className="flex items-start gap-2.5">
            <Checkbox
              id="wizard-msa-consent"
              checked={consent}
              onCheckedChange={(v) => setConsent(v === true)}
              className="mt-0.5"
            />
            <Label
              htmlFor="wizard-msa-consent"
              className="text-sm font-normal leading-snug text-gray-600 cursor-pointer"
            >
              I have read and agree to the Field Sales Agent Agreement, and I
              consent to signing it electronically.
            </Label>
          </div>
          <Button
            onClick={doSign}
            disabled={!canSign}
            className="w-full bg-[#E6007E] hover:bg-[#c4006b] text-white rounded-xl py-6 font-semibold"
          >
            {sign.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <PenLine className="w-4 h-4 mr-1.5" /> Sign agreement
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
}

function ReviewGroup({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-gray-100 bg-white overflow-hidden">
      <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          {title}
        </p>
      </div>
      <div className="divide-y divide-gray-50">{children}</div>
    </div>
  );
}

function ReviewRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="px-4 py-2.5 flex items-start justify-between gap-4">
      <span className="text-sm text-gray-500 shrink-0">{label}</span>
      <span className="text-sm font-medium text-[#0B132B] text-right">
        {value || "—"}
      </span>
    </div>
  );
}
